// @flow
// Загрузка лекал папкой. Из выбранной или перетащенной папки собираем список
// лекал: каждый файл .prn — лекало, название — имя файла. К нему картинка
// (.jpg, .png) и исходник (.cdr, .ai) из той же папки. Внутри могут быть
// вложенные папки с любыми названиями — лекала берутся из всех.
export const CUT_FILE = /\.prn$/i;
const IMAGE_FILE = /\.(jpe?g|png)$/i;
const SOURCE_FILE = /\.(cdr|ai)$/i;
// Служебные файлы Windows и macOS: Thumbs.db, desktop.ini, .DS_Store
const SYSTEM_FILE = /^(\..*|thumbs\.db|desktop\.ini)$/i;

// Путь файла внутри выбранного. react-dropzone кладёт его в path
// («/Папка/Back.prn»), выбор папки кнопкой — в webkitRelativePath.
export function relativePath(file: Object): string {
  return String(file.webkitRelativePath || file.path || file.name)
    .replace(/\\/g, '/')
    .replace(/^\/+/, '');
}

export function hasFolders(files: Array<Object>): boolean {
  return files.some(file => relativePath(file).includes('/'));
}

function withoutTop(path) {
  const i = path.indexOf('/');
  return i < 0 ? path : path.slice(i + 1);
}

function stemOf(name) {
  return name.replace(/\.[^.]+$/, '');
}

export function nameKey(name: string): string {
  return name.trim().toLowerCase();
}

const byName = (a, b) => a.localeCompare(b, 'ru', { numeric: true });

// Файлы по папкам верхнего уровня: { folders: Map(имя папки → файлы), loose }
export function splitTopFolders(files: Array<Object>) {
  const folders = new Map();
  const loose = [];
  files.forEach(file => {
    const path = relativePath(file);
    const i = path.indexOf('/');
    if (i < 0) {
      loose.push(file);
      return;
    }
    const top = path.slice(0, i);
    if (!folders.has(top)) folders.set(top, []);
    folders.get(top).push(file);
  });
  return { folders, loose };
}

// Файлы для списка лекал с путями относительно выбранной папки: её
// собственное имя в колонке «Папка» не нужно
export function filesInsideFolder(files: Array<Object>) {
  const { folders, loose } = splitTopFolders(files);
  const single = folders.size === 1 && loose.length === 0;
  return files.map(file => ({
    file,
    path: single ? withoutTop(relativePath(file)) : relativePath(file),
  }));
}

export function filesInsideTop(files: Array<Object>) {
  return files.map(file => ({ file, path: withoutTop(relativePath(file)) }));
}

// Список лекал. Картинка к лекалу — с тем же именем. Если в папке одно
// лекало и одна картинка, берём её, как бы она ни называлась. Исходник так же.
export function planLekala(entries: Array<{ file: Object, path: string }>) {
  const dirs = new Map();
  let other = 0;

  entries.forEach(({ file, path }) => {
    const parts = path.split('/');
    const base = parts.pop();
    const dir = parts.join('/');
    if (SYSTEM_FILE.test(base)) return;
    if (!dirs.has(dir)) dirs.set(dir, { cuts: [], images: [], sources: [] });
    const group = dirs.get(dir);
    const entry = { file, base, stem: stemOf(base).trim() };
    if (CUT_FILE.test(base)) group.cuts.push(entry);
    else if (IMAGE_FILE.test(base)) group.images.push(entry);
    else if (SOURCE_FILE.test(base)) group.sources.push(entry);
    else other += 1;
  });

  const items = [];
  const warnings = [];
  const taken = new Map();
  const place = dir => (dir ? `«${dir}»` : 'основной папки');

  Array.from(dirs.keys()).sort(byName).forEach(dir => {
    const { cuts, images, sources } = dirs.get(dir);
    const pick = (list, cut) => {
      const same = list.find(x => nameKey(x.stem) === nameKey(cut.stem));
      if (same) return same;
      return cuts.length === 1 && list.length === 1 ? list[0] : null;
    };
    const usedImages = new Set();

    cuts.sort((a, b) => byName(a.base, b.base)).forEach(cut => {
      const image = pick(images, cut);
      const source = pick(sources, cut);
      if (image) usedImages.add(image);
      const key = nameKey(cut.stem);
      if (taken.has(key)) {
        warnings.push(`Лекало «${cut.stem}» есть в двух папках: взято из ${place(taken.get(key))}, из ${place(dir)} пропущено`);
        return;
      }
      taken.set(key, dir);
      items.push({
        name: cut.stem,
        dir,
        cut: cut.file,
        preview: image ? image.file : null,
        source: source ? source.file : null,
      });
    });

    images.filter(image => !usedImages.has(image)).sort((a, b) => byName(a.base, b.base)).forEach(image => {
      warnings.push(`Картинка без лекала: ${dir ? `${dir}/` : ''}${image.base}`);
    });
  });

  if (other) warnings.push(`Пропущено других файлов: ${other}`);
  return { items, warnings };
}
